import {HttpErrorResponse} from '@angular/common/http';
import {concat, forkJoin, Observable, of, Subject, timer} from 'rxjs';
import {catchError, map, shareReplay, switchMap} from 'rxjs/operators';
import {multiScan} from 'rxjs-multi-scan';

import {ListState, StreamInput} from 'shared/interfaces';
import {ContentState} from 'shared/types';
import {ContentStatus} from 'shared/enums';
// import {Model} from 'shared/models';

// import {environment} from '@environments/environment';
// import {ApplicationError} from './application-error';
// import {ContentState, Ok} from '@shared/types';
// import {ListState, StreamInput} from '@shared/interfaces';
// import {ContentStatus} from '@shared/enums';
// import {Model} from '@shared/models';

export const handle = <T>(): ((
  source$: Observable<StreamInput<T>>
) => Observable<ContentState<T>>) => {
  return (source$: Observable<StreamInput<T>>): Observable<ContentState<T>> =>
    source$.pipe(
      switchMap(({status, observable$}: StreamInput<T>) => {
        if (status === ContentStatus.INITIATED) {
          return of<ContentState<T>>({status});
        }

        const forkSources$: Observable<any>[] = [];
        if (observable$) {
          forkSources$.push(
            observable$.pipe(
              map((data: T): ContentState<T> => {
                return {
                  status: ContentStatus.OK,
                  value: data,
                };
              }),
              catchError(
                (
                  // @TODO
                  // error: HttpErrorResponse | ApplicationError
                  error: HttpErrorResponse
                ): Observable<ContentState<T>> => {
                  return of<ContentState<T>>({
                    status: ContentStatus.ERROR,
                    error,
                  });
                }
              )
            )
          );
        } else {
          forkSources$.push(of<ContentState<T>>({status: ContentStatus.ERROR}));
        }
        // @TODO
        // forkSources$.push(timer(environment.minDelay));
        forkSources$.push(timer(500));

        return concat(
          of<ContentState<T>>({status: ContentStatus.LOADING}),
          forkJoin(forkSources$).pipe(
            map((results: any[]): ContentState<T> => results[0])
          )
        );
      }),
      shareReplay(1)
    );
};

export const handleMultiScan = <T, U>({
  add,
  update,
  remove,
}: {
  add?: Subject<U>;
  update?: Subject<{model: U; oldModel: U}>;
  remove?: Subject<U>;
}): ((source$: Observable<StreamInput<T>>) => Observable<ContentState<T>>) => {
  return (source$: Observable<StreamInput<T>>): Observable<ContentState<T>> => {
    const args: any[] = [
      source$.pipe(
        switchMap(({status, observable$}: StreamInput<T>) => {
          if (status === ContentStatus.INITIATED) {
            return of<ContentState<T>>({status});
          }

          const forkSources$: Observable<any>[] = [];
          if (observable$) {
            forkSources$.push(
              observable$.pipe(
                map((data: T): ContentState<T> => {
                  return {
                    status: ContentStatus.OK,
                    value: data,
                  };
                }),
                catchError(
                  (
                    // @TODO
                    // error: HttpErrorResponse | ApplicationError
                    error: HttpErrorResponse
                  ): Observable<ContentState<T>> => {
                    return of<ContentState<T>>({
                      status: ContentStatus.ERROR,
                      error,
                    });
                  }
                )
              )
            );
          } else {
            forkSources$.push(
              of<ContentState<T>>({status: ContentStatus.ERROR})
            );
          }
          // @TODO
          // forkSources$.push(timer(environment.minDelay));
          forkSources$.push(timer(500));

          return concat(
            of<ContentState<T>>({status: ContentStatus.LOADING}),
            forkJoin(forkSources$).pipe(
              map((results: any[]): ContentState<T> => results[0])
            )
          );
        })
      ),
      (previousState: ContentState<T>, newState: ContentState<T>) => {
        // console.info('query reducer', previousState, newState);
        return newState;
      },
    ];
    if (add) {
      args.push(add);
      args.push((state: ContentState<T>, newModel: U) => {
        const value: ListState<U> = state.value as ListState<U>;
        // const value: ListState<U> | null = state.status === ContentStatus.OK ? state.value as ListState<U> : null;
        const list: U[] = value.list as U[];
        (state.value as ListState<U>).list = [...[newModel], ...list];
        return state;
      });
    }
    if (update) {
      args.push(update);
      args.push(
        (
          state: ContentState<T>,
          {model, oldModel}: {model: U; oldModel: U}
        ) => {
          const value: ListState<U> = state.value as ListState<U>;
          const list: U[] = value.list as U[];
          if (Array.isArray(list)) {
            const pos: number = list.findIndex((m: U) => {
              return (
                // (m as unknown as Model).id === (oldModel as unknown as Model).id
                (m as unknown as any).id === (oldModel as unknown as any).id
              );
            });
            if (pos !== -1) {
              list[pos] = model;
            }
          }
          return state;
        }
      );
    }
    if (remove) {
      args.push(remove);
      args.push((state: ContentState<T>, model?: U) => {
        const value: ListState<U> = state.value as ListState<U>;
        const list: U[] = value.list as U[];
        const pos: number = list.findIndex((m: U) => {
          // return (m as unknown as Model).id === (model as unknown as Model).id;
          return (m as unknown as any).id === (model as unknown as any).id;
        });
        if (pos !== -1) {
          list.splice(pos, 1);
        }
        return state;
      });
    }
    args.push({status: ContentStatus.INITIATED});
    // @ts-expect-error foo
    // eslint-disable-next-line prefer-spread
    return multiScan.apply(null, args).pipe(shareReplay(1));
  };
};
